import { computed, inject, Injectable, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

type MarkdownRenderState = {
  inUl: boolean;
  inOl: boolean;
  inTable: boolean;
  paragraphBuffer: string[];
  lastHeading: string;
};

@Injectable({
  providedIn: 'root',
})
export class MarkdownRendererService {
  private readonly sanitizer = inject(DomSanitizer);

  // Signal for caching rendered content
  private readonly renderCache = signal<Map<string, SafeHtml>>(new Map());

  private createActionListRegex(global = false): RegExp {
    const flags = global ? 'g' : '';
    return new RegExp(
      '\\{\\s*"type"\\s*:\\s*"action_list"\\s*,\\s*"itemList"\\s*:\\s*\\[(?:[\\s\\S]*?)\\]\\s*\\}',
      flags
    );
  }

  /** SD-2674: `<chart>{json}</chart>` blocks emitted inline by Allo. */
  private createChartBlockRegex(): RegExp {
    return /<chart>([\s\S]*?)<\/chart>/gi;
  }

  private createActionLinkRegex(global = false): RegExp {
    const flags = global ? 'gi' : 'i';
    return new RegExp(
      '<action-link>\\s*(\\{[\\s\\S]*?\\})\\s*<\\/action-link>',
      flags
    );
  }

  // Signal-based rendering with caching
  renderToHtmlSignal(text: string) {
    return computed(() => {
      const cache = this.renderCache();
      const cached = cache.get(text);
      if (cached) {
        return cached;
      }

      const rendered = this.renderToHtml(text);
      this.renderCache.update(cache => {
        const newCache = new Map(cache);
        newCache.set(text, rendered);
        return newCache;
      });

      return rendered;
    });
  }

  renderToHtml(text: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      this.renderToHtmlString(text)
    );
  }

  /**
   * The same markup as {@link renderToHtml}, as a string.
   *
   * SD-2708: the send-email card puts the body on the clipboard as `text/html` so formatting
   * survives the paste into Outlook or Gmail. SafeHtml is opaque — good for binding, useless for
   * writing to a clipboard flavour — so the string form is exposed rather than rendering twice.
   */
  renderToHtmlString(text: string): string {
    if (!text?.trim()) return '';
    return this.markdownToHtml(this.processCustomComponents(text));
  }

  private processCustomComponents(text: string): string {
    let processed = text;

    processed = this.processActionLinks(processed);

    // Strip assistant control tags that shouldn't be displayed
    processed = processed.replace(/<\/?(tool|response)>/gi, '');

    // Handle <thinking> blocks
    processed = processed.replace(
      /<thinking>(.*?)<\/thinking>/gs,
      (match, content) => {
        const cleanContent = content.trim();
        return `<div class="custom-component" data-type="thinking" data-content="${this.escapeHtml(cleanContent)}"></div>`;
      }
    );

    // Handle JSON action lists and action cards in code fences
    processed = processed.replace(
      /```json\s*(\{[\s\S]*?\})\s*```/gi,
      (match, jsonPayload) => {
        try {
          const data = JSON.parse(jsonPayload);
          if (data?.type === 'action_list' && Array.isArray(data?.itemList)) {
            const escapedData = this.escapeHtml(JSON.stringify(data));
            return `<div class="custom-component" data-type="action_list" data-content="${escapedData}"></div>`;
          }
          // Strip code-fenced action cards (rendered via extractCustomComponents → Angular component)
          if (this.isKnownActionCardType(this.getNormalizedType(data))) {
            return '';
          }
        } catch (error) {
          console.warn('Failed to parse code block JSON:', error);
        }
        return match;
      }
    );

    processed = processed.replace(this.createActionListRegex(true), match => {
      try {
        const data = JSON.parse(match);
        const escapedData = this.escapeHtml(JSON.stringify(data));
        return `<div class="custom-component" data-type="action_list" data-content="${escapedData}"></div>`;
      } catch (error) {
        console.warn('Failed to parse action list JSON:', error);
        return match; // Return original if parsing fails
      }
    });

    // Strip <action-card>...</action-card> from HTML render path.
    // Action cards are handled separately via extractCustomComponents() → Angular component.
    processed = processed.replace(/<action-card>[\s\S]*?<\/action-card>/gi, '');

    // SD-2674: strip <chart>...</chart> from the HTML render path. Charts are
    // handled separately via extractCustomComponents() → Angular component, so
    // the raw spec JSON must never be rendered as literal text here.
    processed = processed.replace(this.createChartBlockRegex(), '');

    // Defensive for v1/resync streams: a trailing <chart> whose closing tag has
    // not arrived yet would otherwise leak partial JSON into the bubble. The
    // bubble renders a "Building chart…" placeholder for it instead.
    processed = processed.replace(/<chart>[\s\S]*$/i, '');

    // Remove stray closing thinking tags if any remain
    processed = processed.replace(/<\/thinking>/gi, '');

    return processed;
  }

  private processActionLinks(text: string): string {
    type ParsedActionLink = {
      title: string;
      route: string;
      description?: string;
    };

    let processed = '';
    let lastIndex = 0;

    for (const match of text.matchAll(this.createActionLinkRegex(true))) {
      const fullMatch = match[0];
      const jsonPayload = match[1];
      const matchIndex = match.index ?? -1;

      if (matchIndex < 0) {
        continue;
      }

      let segment = text.slice(lastIndex, matchIndex);
      const parsedLink = this.parseActionLink(jsonPayload);
      if (parsedLink) {
        segment = this.replaceActionLinkTitle(segment, parsedLink);
      }

      processed += segment;
      lastIndex = matchIndex + fullMatch.length;
    }

    processed += text.slice(lastIndex);

    return processed;
  }

  private parseActionLink(jsonPayload: string): {
    title: string;
    route: string;
    description?: string;
  } | null {
    try {
      const parsed = JSON.parse(jsonPayload) as {
        title?: unknown;
        description?: unknown;
        actions?: {
          type?: unknown;
          params?: { dealId?: unknown; path?: unknown };
        };
      };

      const title = typeof parsed.title === 'string' ? parsed.title.trim() : '';
      const description =
        typeof parsed.description === 'string' ? parsed.description.trim() : '';
      const actionType =
        typeof parsed.actions?.type === 'string' ? parsed.actions.type : '';
      const dealIdRaw = parsed.actions?.params?.dealId;
      const dealId = typeof dealIdRaw === 'string' ? dealIdRaw.trim() : '';
      const pathRaw = parsed.actions?.params?.path;
      const basePath =
        typeof pathRaw === 'string' && pathRaw.trim()
          ? pathRaw.trim().replace(/^\/+|\/+$/g, '')
          : 'itms/originations';

      if (!title || actionType !== 'deal-details' || !dealId) {
        return null;
      }

      const encodedDealId = this.base64Encode(dealId);
      return {
        title,
        route: `/${basePath}/deal/${encodeURIComponent(encodedDealId)}`,
        description: description || undefined,
      };
    } catch (error) {
      console.warn('Failed to parse action-link JSON:', error);
      return null;
    }
  }

  private replaceActionLinkTitle(
    segment: string,
    link: { title: string; route: string; description?: string }
  ): string {
    const markdownLink = this.buildActionLinkMarkdown(link);

    const boldReplacement =
      this.replaceLastLiteral(
        segment,
        `**${link.title}**`,
        `**${markdownLink}**`
      ) ??
      this.replaceLastLiteral(
        segment,
        `__${link.title}__`,
        `__${markdownLink}__`
      );

    if (boldReplacement) {
      return boldReplacement;
    }

    return this.replaceLastPlainTitle(segment, link.title, markdownLink);
  }

  private buildActionLinkMarkdown(link: {
    title: string;
    route: string;
    description?: string;
  }): string {
    const markdownLink = `[${link.title}](${link.route})`;
    if (!link.description) {
      return markdownLink;
    }

    return `${markdownLink}{qtTooltip:${encodeURIComponent(link.description)}}`;
  }

  private replaceLastLiteral(
    text: string,
    search: string,
    replacement: string
  ): string | null {
    const index = text.lastIndexOf(search);
    if (index === -1) {
      return null;
    }

    return `${text.slice(0, index)}${replacement}${text.slice(index + search.length)}`;
  }

  private replaceLastPlainTitle(
    text: string,
    title: string,
    replacement: string
  ): string {
    const pattern = new RegExp(
      `(^|[^\\w])(${this.escapeRegExp(title)})(?=$|[^\\w])`,
      'g'
    );

    let lastMatch: RegExpMatchArray | undefined;
    for (const match of text.matchAll(pattern)) {
      lastMatch = match;
    }

    if (lastMatch?.index === undefined) {
      return text;
    }

    const prefixLength = lastMatch[1]?.length ?? 0;
    const matchStart = lastMatch.index + prefixLength;

    return `${text.slice(0, matchStart)}${replacement}${text.slice(matchStart + title.length)}`;
  }

  /**
   * Checks if text contains custom components that need special rendering
   */
  hasCustomComponents(text: string): boolean {
    return (
      /<thinking>.*?<\/thinking>/.test(text) ||
      this.createActionListRegex().test(text) ||
      /<action-card>/i.test(text) ||
      /<chart>/i.test(text) ||
      this.createActionLinkRegex().test(text)
    );
  }

  /**
   * Signal-based method to check for custom components
   */
  hasCustomComponentsSignal(text: string) {
    return computed(() => this.hasCustomComponents(text));
  }

  /**
   * Extracts custom component data from text
   */
  extractCustomComponents(
    text: string
  ): Array<{ type: string; data: unknown; originalMatch: string }> {
    const components: Array<{
      type: string;
      data: unknown;
      originalMatch: string;
    }> = [];
    const seenActionLists = new Set<string>();

    this.extractThinkingComponents(text, components);
    this.extractFencedJsonComponents(text, components, seenActionLists);
    this.extractActionListComponents(text, components, seenActionLists);
    this.extractActionCardComponents(text, components);
    this.extractFreestandingActionCards(text, components);
    this.extractChartComponents(text, components);

    return components;
  }

  /**
   * SD-2674: pulls `<chart>{json}</chart>` blocks out as `chart` parts carrying
   * the raw JSON string. The string is parsed and validated downstream by
   * `parseAlloChartSpec` and bound to a component input — it never travels the
   * innerHTML path.
   *
   * A trailing `<chart>` whose closer has not streamed in yet yields a
   * `chart_loading` part so the bubble can show a placeholder.
   */
  private extractChartComponents(
    text: string,
    components: Array<{ type: string; data: unknown; originalMatch: string }>
  ): void {
    const chartRegex = this.createChartBlockRegex();
    let chartMatch: RegExpExecArray | null;

    while ((chartMatch = chartRegex.exec(text)) !== null) {
      components.push({
        type: 'chart',
        data: chartMatch[1].trim(),
        originalMatch: chartMatch[0],
      });
    }

    const unterminated = this.findUnterminatedChartFragment(text);
    if (unterminated) {
      components.push({
        type: 'chart_loading',
        data: '',
        originalMatch: unterminated,
      });
    }
  }

  /** The trailing `<chart>…` fragment still awaiting its closing tag, if any. */
  private findUnterminatedChartFragment(text: string): string | null {
    const openIndex = text.toLowerCase().lastIndexOf('<chart>');
    if (openIndex < 0) {
      return null;
    }

    const tail = text.slice(openIndex);
    return /<\/chart>/i.test(tail) ? null : tail;
  }

  private extractThinkingComponents(
    text: string,
    components: Array<{ type: string; data: unknown; originalMatch: string }>
  ): void {
    const thinkingMatches = text.matchAll(/<thinking>(.*?)<\/thinking>/gs);
    for (const match of thinkingMatches) {
      components.push({
        type: 'thinking',
        data: { content: match[1].trim() },
        originalMatch: match[0],
      });
    }
  }

  private extractFencedJsonComponents(
    text: string,
    components: Array<{ type: string; data: unknown; originalMatch: string }>,
    seenActionLists: Set<string>
  ): void {
    const fencedMatches = text.matchAll(/```json\s*(\{[\s\S]*?\})\s*```/gi);
    for (const match of fencedMatches) {
      try {
        const jsonContent = match[1];
        const data = JSON.parse(jsonContent);
        if (this.isKnownActionCardType(this.getNormalizedType(data))) {
          const normalized = this.ensureActionCardStructure(
            this.normalizeActionCardType(data)
          );
          if (typeof normalized['type'] === 'string') {
            components.push({
              type: 'action_card',
              data: normalized,
              originalMatch: match[0],
            });
            continue;
          }
        }

        this.pushActionListComponent(
          components,
          seenActionLists,
          match[0],
          data,
          jsonContent
        );
      } catch (error) {
        console.warn('Failed to parse JSON from code fence:', error);
      }
    }
  }

  private extractActionListComponents(
    text: string,
    components: Array<{ type: string; data: unknown; originalMatch: string }>,
    seenActionLists: Set<string>
  ): void {
    const actionListMatches = text.matchAll(this.createActionListRegex(true));
    for (const match of actionListMatches) {
      try {
        const data = JSON.parse(match[0]);
        this.pushActionListComponent(
          components,
          seenActionLists,
          match[0],
          data,
          match[0]
        );
      } catch (error) {
        console.warn('Failed to parse action list JSON:', error);
      }
    }
  }

  private extractActionCardComponents(
    text: string,
    components: Array<{ type: string; data: unknown; originalMatch: string }>
  ): void {
    const actionCardRegex = /<action-card>([\s\S]*?)<\/action-card>/gi;
    let actionCardMatch: RegExpExecArray | null;

    while ((actionCardMatch = actionCardRegex.exec(text)) !== null) {
      const normalized = this.parseAndNormalizeActionCard(actionCardMatch[1]);
      if (!normalized) {
        continue;
      }

      components.push({
        type: 'action_card',
        data: normalized,
        originalMatch: actionCardMatch[0],
      });
    }
  }

  // Fallback for raw JSON action cards (no wrapper, no fence); only extracts known types to avoid hijacking user-pasted JSON.
  private extractFreestandingActionCards(
    text: string,
    components: Array<{ type: string; data: unknown; originalMatch: string }>
  ): void {
    const masked = this.maskAlreadyExtractedRegions(text);
    let i = 0;
    while (i < masked.length) {
      if (masked[i] !== '{') {
        i++;
        continue;
      }
      const end = this.findBalancedJsonEnd(masked, i);
      if (end < 0) {
        i++;
        continue;
      }
      const candidate = text.slice(i, end + 1);
      const normalized = this.tryParseAsActionCard(candidate);
      if (normalized) {
        components.push({
          type: 'action_card',
          data: normalized,
          originalMatch: candidate,
        });
      }
      i = end + 1;
    }
  }

  private tryParseAsActionCard(
    candidate: string
  ): Record<string, unknown> | null {
    try {
      const data = JSON.parse(candidate);
      if (data == null || typeof data !== 'object') {
        return null;
      }
      if (!this.isKnownActionCardType(this.getNormalizedType(data))) {
        return null;
      }
      const normalized = this.ensureActionCardStructure(
        this.normalizeActionCardType(data as Record<string, unknown>)
      );
      if (typeof normalized['type'] !== 'string') {
        return null;
      }
      return normalized;
    } catch {
      return null;
    }
  }

  // Blanks out wrapped/fenced regions with spaces so freestanding-JSON offsets still align with the original text.
  private maskAlreadyExtractedRegions(text: string): string {
    const blank = (match: string) => ' '.repeat(match.length);
    return text
      .replace(/<action-card>[\s\S]*?<\/action-card>/gi, blank)
      .replace(/```[\s\S]*?```/g, blank);
  }

  // Index of the `}` closing the JSON object at `start`, or -1; ignores braces inside string literals.
  private findBalancedJsonEnd(text: string, start: number): number {
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = start; i < text.length; i++) {
      const ch = text[i];
      if (inString) {
        if (escaped) {
          escaped = false;
        } else if (ch === '\\') {
          escaped = true;
        } else if (ch === '"') {
          inString = false;
        }
        continue;
      }
      if (ch === '"') {
        inString = true;
      } else if (ch === '{') {
        depth++;
      } else if (ch === '}') {
        depth--;
        if (depth === 0) return i;
      }
    }
    return -1;
  }

  private pushActionListComponent(
    components: Array<{ type: string; data: unknown; originalMatch: string }>,
    seenActionLists: Set<string>,
    originalMatch: string,
    data: unknown,
    dedupeKey: string
  ): void {
    if (seenActionLists.has(dedupeKey)) {
      return;
    }

    seenActionLists.add(dedupeKey);
    components.push({
      type: 'action_list',
      data,
      originalMatch,
    });
  }

  private getNormalizedType(data: unknown): string {
    if (data == null || typeof data !== 'object') {
      return '';
    }

    const type = (data as Record<string, unknown>)['type'];
    if (typeof type !== 'string') {
      return '';
    }

    return type.toLowerCase().replace(/[-_ ]/g, '');
  }

  /**
   * SD-2865: normalise one action-card envelope that arrived as structured data
   * rather than embedded in the reply text — from the `action_card` chatStatus
   * frame or the persisted event's `actionCards`. Same parsing and normalisation
   * the `<action-card>` path uses, so both produce identical card parts.
   *
   * @returns the normalised card, or null when the JSON is unusable
   */
  parseStructuredActionCard(rawJson: string): Record<string, unknown> | null {
    return this.parseAndNormalizeActionCard(rawJson);
  }

  private parseAndNormalizeActionCard(
    rawActionCardContent: string
  ): Record<string, unknown> | null {
    try {
      const parsed = this.parseActionCardJson(rawActionCardContent.trim());
      if (!parsed) {
        return null;
      }

      const normalized = this.normalizeActionCardType(parsed);
      if (typeof normalized['type'] !== 'string') {
        return null;
      }

      return this.ensureActionCardStructure(normalized);
    } catch (error) {
      console.warn('Failed to parse action-card JSON:', error);
      return null;
    }
  }

  private parseActionCardJson(jsonStr: string): Record<string, unknown> | null {
    try {
      return JSON.parse(jsonStr);
    } catch {
      const jsonObjMatch = this.extractJsonObject(jsonStr);
      if (!jsonObjMatch) {
        return null;
      }
      return JSON.parse(jsonObjMatch[0]);
    }
  }

  private extractJsonObject(str: string): string | null {
    const start = str.indexOf('{');
    if (start === -1) return null;

    let depth = 0;

    for (let i = start; i < str.length; i++) {
      if (str[i] === '{') depth++;
      else if (str[i] === '}') depth--;

      if (depth === 0) {
        return str.slice(start, i + 1);
      }
    }

    return null;
  }

  // Canonical types the FE action-card template branches on; kept in sync with `action-card.types.ts` guards.
  private static readonly KNOWN_ACTION_CARD_TYPES: Record<string, string> = {
    sendemail: 'send-email',
    createnote: 'create-note',
    createtask: 'create-task',
    downloadword: 'download-word',
    downloadexcel: 'download-excel',
  };

  private isKnownActionCardType(rawType: string): boolean {
    return rawType in MarkdownRendererService.KNOWN_ACTION_CARD_TYPES;
  }

  private normalizeActionCardType(
    data: Record<string, unknown>
  ): Record<string, unknown> {
    const rawType = this.getNormalizedType(data);
    const canonical = MarkdownRendererService.KNOWN_ACTION_CARD_TYPES[rawType];
    if (canonical) {
      return { ...data, type: canonical };
    }

    return data;
  }

  private ensureActionCardStructure(
    data: Record<string, unknown>
  ): Record<string, unknown> {
    if (!data['card'] && (data['subject'] || data['content'])) {
      return {
        ...data,
        card: {
          subject: data['subject'],
          content: data['content'],
          title: data['title'] ?? 'Send email',
          button: data['button'] ?? 'Send Email',
          recipients: data['recipients'],
        },
      };
    }

    if (data['type'] === 'create-note' && !data['card']) {
      return {
        ...data,
        card: {
          title: data['title'] ?? 'New note',
          content: data['content'],
          button: data['button'] ?? 'Save Note',
        },
      };
    }

    if (data['type'] === 'create-task' && !data['card']) {
      return {
        ...data,
        card: {
          title: data['title'] ?? 'New task',
          description: data['description'],
          dueDate: data['dueDate'],
          assignee: data['assignee'],
          button: data['button'] ?? 'Create Task',
        },
      };
    }

    return data;
  }

  /**
   * Signal-based method to extract custom components
   */
  extractCustomComponentsSignal(text: string) {
    return computed(() => this.extractCustomComponents(text));
  }

  private markdownToHtml(text: string): string {
    const normalized = text.replace(/\r\n/g, '\n');
    const lines = normalized.split('\n');
    const htmlParts: string[] = [];
    const state: MarkdownRenderState = {
      inUl: false,
      inOl: false,
      inTable: false,
      paragraphBuffer: [],
      lastHeading: '',
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i] ?? '';
      const nextLine = i + 1 < lines.length ? (lines[i + 1] ?? '') : null;
      const consumedNextLine = this.processMarkdownLine(
        line,
        nextLine,
        htmlParts,
        state
      );
      if (consumedNextLine) {
        i++;
      }
    }

    this.flushParagraph(htmlParts, state);
    this.closeLists(htmlParts, state);
    this.closeTable(htmlParts, state);

    return htmlParts.join('');
  }

  private processMarkdownLine(
    line: string,
    nextLine: string | null,
    htmlParts: string[],
    state: MarkdownRenderState
  ): boolean {
    if (this.tryStartTable(line, nextLine, htmlParts, state)) {
      return true;
    }

    if (this.tryAppendTableRow(line, htmlParts, state)) {
      return false;
    }

    if (this.finalizeTableForNonTableLine(line, htmlParts, state)) {
      return false;
    }

    this.processNonTableLine(line, htmlParts, state);
    return false;
  }

  private processNonTableLine(
    line: string,
    htmlParts: string[],
    state: MarkdownRenderState
  ): void {
    const unorderedMatch = /^\s*[-*]\s+(.+)/.exec(line);
    if (unorderedMatch) {
      this.flushParagraph(htmlParts, state);
      this.closeOl(htmlParts, state);
      this.openUlIfNeeded(htmlParts, state);
      htmlParts.push(`<li>${this.parseInline(unorderedMatch[1])}</li>`);
      return;
    }

    const orderedMatch = /^\s*(\d+)\.\s+(.+)/.exec(line);
    if (orderedMatch) {
      this.flushParagraph(htmlParts, state);
      this.closeUl(htmlParts, state);
      this.openOlIfNeeded(htmlParts, state, orderedMatch[1]);
      htmlParts.push(`<li>${this.parseInline(orderedMatch[2])}</li>`);
      return;
    }

    const headingMatch = /^\s*(#{1,3})\s+(.+)/.exec(line);
    if (headingMatch) {
      const level = Math.min(headingMatch[1].length, 3);
      this.flushParagraph(htmlParts, state);
      this.closeLists(htmlParts, state);
      state.lastHeading = this.toPlainText(headingMatch[2]);
      htmlParts.push(
        `<h${level}>${this.parseInline(headingMatch[2])}</h${level}>`
      );
      return;
    }

    if (!line.trim()) {
      this.flushParagraph(htmlParts, state);
      this.closeLists(htmlParts, state);
      return;
    }

    this.closeLists(htmlParts, state);
    state.paragraphBuffer.push(this.parseInline(line));
  }

  private tryStartTable(
    line: string,
    nextLine: string | null,
    htmlParts: string[],
    state: MarkdownRenderState
  ): boolean {
    if (
      state.inTable ||
      !nextLine ||
      !this.isTableRow(line) ||
      !this.isTableSeparator(nextLine)
    ) {
      return false;
    }

    this.flushParagraph(htmlParts, state);
    this.closeLists(htmlParts, state);
    state.inTable = true;
    htmlParts.push(this.getTableBlockOpenHtml(state.lastHeading));
    htmlParts.push('<div class="table-wrapper">');
    htmlParts.push('<table>');
    htmlParts.push('<thead>');
    htmlParts.push(this.parseTableRow(line, true));
    htmlParts.push('</thead>');
    htmlParts.push('<tbody>');
    return true;
  }

  private tryAppendTableRow(
    line: string,
    htmlParts: string[],
    state: MarkdownRenderState
  ): boolean {
    if (!state.inTable || !this.isTableRow(line)) {
      return false;
    }

    htmlParts.push(this.parseTableRow(line, false));
    return true;
  }

  private finalizeTableForNonTableLine(
    line: string,
    htmlParts: string[],
    state: MarkdownRenderState
  ): boolean {
    if (!state.inTable) {
      return false;
    }

    this.closeTable(htmlParts, state);
    return !line.trim();
  }

  private closeUl(htmlParts: string[], state: MarkdownRenderState): void {
    if (!state.inUl) {
      return;
    }
    htmlParts.push('</ul>');
    state.inUl = false;
  }

  private closeOl(htmlParts: string[], state: MarkdownRenderState): void {
    if (!state.inOl) {
      return;
    }
    htmlParts.push('</ol>');
    state.inOl = false;
  }

  private closeLists(htmlParts: string[], state: MarkdownRenderState): void {
    this.closeUl(htmlParts, state);
    this.closeOl(htmlParts, state);
  }

  private openUlIfNeeded(
    htmlParts: string[],
    state: MarkdownRenderState
  ): void {
    if (state.inUl) {
      return;
    }
    htmlParts.push('<ul>');
    state.inUl = true;
  }

  private openOlIfNeeded(
    htmlParts: string[],
    state: MarkdownRenderState,
    startNumberRaw: string
  ): void {
    if (state.inOl) {
      return;
    }
    const startNum = parseInt(startNumberRaw, 10);
    htmlParts.push(startNum > 1 ? `<ol start="${startNum}">` : '<ol>');
    state.inOl = true;
  }

  private flushParagraph(
    htmlParts: string[],
    state: MarkdownRenderState
  ): void {
    if (!state.paragraphBuffer.length) {
      return;
    }
    htmlParts.push(`<p>${state.paragraphBuffer.join('<br/>')}</p>`);
    state.paragraphBuffer = [];
  }

  private isTableRow(line: string): boolean {
    const trimmed = line.trim();
    return trimmed.includes('|') && trimmed.split('|').length > 2;
  }

  private isTableSeparator(line: string): boolean {
    const trimmed = line.trim();
    return /^\|?[\s\-:|]+\|[\s\-:|]+\|?$/.test(trimmed);
  }

  private parseTableRow(line: string, isHeader: boolean): string {
    const cells = line
      .trim()
      .split('|')
      .map(cell => cell.trim())
      .filter(
        (cell, index, array) =>
          cell !== '' || (index !== 0 && index !== array.length - 1)
      );

    const tag = isHeader ? 'th' : 'td';
    const cellsHtml = cells
      .map(cell => {
        const content = this.stripActionLinks(this.parseInline(cell));
        const inner = isHeader
          ? `<span class="qt-th-label">${content}</span>`
          : content;
        return `<${tag}>${inner}</${tag}>`;
      })
      .join('');

    return `<tr>${cellsHtml}</tr>`;
  }

  private stripActionLinks(html: string): string {
    return html.replace(
      /<a class="qt-action-link"[^>]*>([\s\S]*?)<\/a>/g,
      '$1'
    );
  }

  private closeTable(htmlParts: string[], state: MarkdownRenderState): void {
    if (!state.inTable) {
      return;
    }
    htmlParts.push('</tbody>');
    htmlParts.push('</table>');
    htmlParts.push('</div>');
    htmlParts.push('<div class="qt-table-block__foot">');
    htmlParts.push(this.getTableExportButtonHtml());
    htmlParts.push('</div>');
    htmlParts.push('</div>');
    state.inTable = false;
  }

  private getTableBlockOpenHtml(title: string): string {
    const titleAttr = title
      ? ` data-table-title="${this.escapeHtml(title)}"`
      : '';
    return (
      `<div class="qt-table-block"${titleAttr}>` +
      '<div class="qt-table-block__bar">' +
      '<span class="qt-table-block__count"></span>' +
      '<button type="button" class="qt-table-expand-btn" data-action="expand-table" aria-label="Expand table">' +
      '<span class="mat-icon notranslate material-symbols-outlined mat-ligature-font qt-table-expand-btn__icon" aria-hidden="true">open_in_full</span>' +
      '<span class="qt-table-expand-btn__label">Expand</span>' +
      '</button>' +
      '</div>'
    );
  }

  private toPlainText(value: string): string {
    return value
      .replace(/`([^`]*)`/g, '$1')
      .replace(/\*\*([^*]*)\*\*/g, '$1')
      .replace(/\*([^*]*)\*/g, '$1')
      .replace(/__([^_]*)__/g, '$1')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      .trim();
  }

  private getTableExportButtonHtml(): string {
    return '<button type="button" class="allo-export qt-table-export-btn" data-action="export-table" aria-label="Download table as CSV"><span class="mat-icon notranslate material-symbols-outlined mat-ligature-font download-icon qt-table-export-btn__icon" aria-hidden="true">download</span><span class="qt-table-export-btn__label">Download CSV</span></button>';
  }

  private parseInline(value: string): string {
    let escaped = this.escapeHtml(value);

    // Handle code blocks first to avoid conflicts
    const codePlaceholders: string[] = [];
    escaped = escaped.replace(/`([^`]+)`/g, (_match, code) => {
      const placeholder = `@@CODE${codePlaceholders.length}@@`;
      codePlaceholders.push(`<code>${this.escapeHtml(code)}</code>`);
      return placeholder;
    });

    // Handle links
    const linkPlaceholders: string[] = [];
    escaped = escaped.replace(
      /\[([^\]]+)\]\(((?:https?:\/\/|\/|itms\/)[^\s)]+)\)(?:\{qtTooltip:([^}]+)\})?/g,
      (_match, label, url, tooltipToken) => {
        const placeholder = `@@LINK${linkPlaceholders.length}@@`;
        const safeUrl = this.escapeHtml(url);
        const safeLabel = this.escapeHtml(label);
        const isExternal = /^https?:\/\//i.test(url);
        const tooltip = this.decodeTooltipToken(tooltipToken);
        const tooltipAttr = tooltip
          ? ` title="${this.escapeHtml(tooltip)}"`
          : '';

        linkPlaceholders.push(
          isExternal
            ? `<a href="${safeUrl}"${tooltipAttr} target="_blank" rel="noopener noreferrer">${safeLabel}</a>`
            : `<a class="qt-action-link" href="${safeUrl}"${tooltipAttr}>${safeLabel}</a>`
        );
        return placeholder;
      }
    );

    // Bold text
    escaped = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    escaped = escaped.replace(/__([^_]+)__/g, '<strong>$1</strong>');

    // Italic text
    escaped = escaped.replace(/\*(?!\s)([^*]+?)\*(?!\s)/g, '<em>$1</em>');
    escaped = escaped.replace(/_(?!\s)([^_]+?)_(?!\s)/g, '<em>$1</em>');

    // Restore placeholders
    linkPlaceholders.forEach((html, index) => {
      escaped = escaped.replace(`@@LINK${index}@@`, html);
    });

    codePlaceholders.forEach((html, index) => {
      escaped = escaped.replace(`@@CODE${index}@@`, html);
    });

    return escaped;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private decodeTooltipToken(value: string | undefined): string | null {
    if (!value) {
      return null;
    }

    try {
      const decoded = decodeURIComponent(value).trim();
      return decoded || null;
    } catch {
      const trimmed = value.trim();
      return trimmed || null;
    }
  }

  private base64Encode(value: string): string {
    try {
      return btoa(value);
    } catch {
      return value;
    }
  }
}
